import { useState, useMemo, useCallback } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@components/ui';
import Avatar from '@components/ui/Avatar/Avatar';
import Tabs from '@components/ui/Tabs/Tabs';
import type { TabItem } from '@components/ui/Tabs/Tabs';
import DataTable from '@components/shared/DataTable/DataTable';
import type {
  DataTableColumn,
  SortState,
} from '@components/shared/DataTable/DataTable';
import FilterPanel from '@components/shared/FilterPanel/FilterPanel';
import AppliedFiltersBar from '@components/shared/AppliedFiltersBar/AppliedFiltersBar';
import { getEmployees, getEmployeeCounts } from '@/services/employeeService';
import { getLegalEntities } from '@/services/legalEntityService';
import { getCommercialTerms } from '@/services/commercialTermService';
import { useTableFilters } from '@/hooks/useTableFilters';
import type { EmployeeDto } from '@/types/employee';
import type { FilterFieldConfig } from '@/types/filters';
import { useAuthStore } from '@/stores/authStore';
import Popover from '@components/ui/Popover/Popover';
import PlusIcon from '@assets/icons/PlusIcon';
import UsersEmptyIcon from '@assets/icons/UsersEmptyIcon';
import { validateTableSearch, buildSortString, toPaginationState } from '@utils/tabHelpers';
import { toTitleCase } from '@utils/textCase';
import type { TableSearchParams } from '@utils/tabHelpers';
import { useCountries } from '@/hooks/useCountries';
import CreateEmployeeModal from '@features/employees/CreateEmployeeModal/CreateEmployeeModal';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import styles from './index.module.scss';

export const Route = createFileRoute('/t/employees/')({
  validateSearch: (search: Record<string, unknown>) =>
    validateTableSearch(search, { tab: 'ACTIVE' }),
  component: EmployeesPage,
});

function statusBadgeClass(status: EmployeeDto['status'], styles: Record<string, string>): string {
  switch (status) {
    case 'ACTIVE':
      return styles.badgeActive!;
    case 'PENDING_CONTRACT':
      return styles.badgeWarning!;
    case 'PENDING_ONBOARDING':
    case 'ACTIVATION_PENDING':
      return styles.badgeInfo!;
    case 'INACTIVE':
      return styles.badgeInactive!;
  }
}

function StatusBadge({ status }: { status: EmployeeDto['status'] }) {
  return (
    <span className={`${styles.badge} ${statusBadgeClass(status, styles)}`}>
      {toTitleCase(status)}
    </span>
  );
}

function EmployeesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { page: urlPage, size: urlSize, sortBy, sortDir, filters: filtersParam, tab } = Route.useSearch();
  const hasPermission = useAuthStore((s) => s.hasPermission);

  const page = urlPage ?? 0;
  const size = urlSize ?? 10;
  const activeTab = tab ?? 'ACTIVE';

  const { data: countsData } = useQuery({
    queryKey: ['employeeCounts'],
    queryFn: getEmployeeCounts,
  });

  const tabCounts = useMemo(() => {
    if (!countsData) return undefined;
    const total =
      (countsData.ACTIVE ?? 0) +
      (countsData.PENDING_CONTRACT ?? 0) +
      (countsData.PENDING_ONBOARDING ?? 0) +
      (countsData.ACTIVATION_PENDING ?? 0) +
      (countsData.INACTIVE ?? 0);
    return {
      ACTIVE: countsData.ACTIVE ?? 0,
      PENDING_CONTRACT: countsData.PENDING_CONTRACT ?? 0,
      PENDING_ONBOARDING: countsData.PENDING_ONBOARDING ?? 0,
      ACTIVATION_PENDING: countsData.ACTIVATION_PENDING ?? 0,
      INACTIVE: countsData.INACTIVE ?? 0,
      ALL: total,
    };
  }, [countsData]);

  const statusTabs = useMemo<TabItem[]>(
    () => [
      { id: 'ACTIVE', label: toTitleCase(t('K_STATUS_ACTIVE', 'Active')) },
      { id: 'PENDING_CONTRACT', label: toTitleCase(t('K_STATUS_PENDING_CONTRACT', 'Pending Contract')) },
      { id: 'PENDING_ONBOARDING', label: toTitleCase(t('K_STATUS_PENDING_ONBOARDING', 'Pending Onboarding')) },
      { id: 'ACTIVATION_PENDING', label: toTitleCase(t('K_STATUS_ACTIVATION_PENDING', 'Activation Pending')) },
      { id: 'INACTIVE', label: toTitleCase(t('K_STATUS_INACTIVE', 'Inactive')) },
      { id: 'ALL', label: t('K_STATUS_ALL', 'All') },
    ],
    [t],
  );

  const handleTabChange = useCallback(
    (tabId: string) => {
      void navigate({
        to: '/t/employees',
        search: {
          tab: tabId,
          page: 0,
          size: urlSize,
        },
        replace: true,
      });
    },
    [navigate, urlSize],
  );

  const sort: SortState | undefined = useMemo(
    () => (sortBy && sortDir ? { columnId: sortBy, direction: sortDir } : undefined),
    [sortBy, sortDir],
  );

  const tenantId = useAuthStore((s) => s.session?.tenant.id);
  // Read from session (frozen at login). Tenant activation never happens from
  // this page, so the only stale-status case is admin activating in another
  // tab while the tenant idles here — tenant has to re-login to enable Add.
  // Accepted edge case in exchange for skipping the fetch on every entry.
  const tenantStatus = useAuthStore((s) => s.session?.tenant.status ?? null);
  const isTenantActive = tenantStatus === 'ACTIVE';

  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleCreate = useCallback(() => setIsModalOpen(true), []);
  const handleModalClose = useCallback(() => setIsModalOpen(false), []);

  const { countries } = useCountries();
  const countryMap = useMemo(() => new Map(countries.map((c) => [c.id, c.name])), [countries]);

  // Resolve employment type names via commercial terms (tenants don't hold EMPLOYMENT_TYPES_VIEW;
  // commercial terms embed the full EmploymentType record and are permission-aligned with tenant users).
  const { data: commercialTermsData } = useQuery({
    queryKey: ['commercialTerms', 'TENANT', tenantId],
    queryFn: () => getCommercialTerms('TENANT', tenantId!, { size: 500 }),
    enabled: !!tenantId,
  });
  const employmentTypeMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const term of commercialTermsData?.content ?? []) {
      if (term.employmentType && !map.has(term.employmentType.id)) {
        map.set(term.employmentType.id, term.employmentType.name);
      }
    }
    return map;
  }, [commercialTermsData]);
  const employmentTypeOptions = useMemo(
    () => Array.from(employmentTypeMap, ([value, label]) => ({ label, value })),
    [employmentTypeMap],
  );

  const { data: legalEntitiesData } = useQuery({
    queryKey: ['legalEntities', { size: 100 }],
    queryFn: () => getLegalEntities({ size: 100 }),
  });
  const legalEntityMap = useMemo(
    () => new Map((legalEntitiesData?.content ?? []).map((le) => [le.id, le.name])),
    [legalEntitiesData],
  );
  const legalEntityOptions = useMemo(
    () => (legalEntitiesData?.content ?? []).map((le) => ({ label: le.name, value: le.id })),
    [legalEntitiesData],
  );

  // Filter fields ordered to match table columns:
  // Name, Email, Country, Job Title, Department, Employment Type, Contract Type, Start Date, ..., Legal Entity, Status
  const filterFields = useMemo<FilterFieldConfig[]>(
    () => [
      {
        fieldKey: 'countryId',
        label: t('K_FILTER_COUNTRY', 'Country'),
        type: 'select',
        options: countries.map((c) => ({ label: c.name, value: c.id })),
      },
      {
        fieldKey: 'department',
        label: t('K_FILTER_DEPARTMENT', 'Department'),
        type: 'text',
        placeholder: t('K_PLACEHOLDER_SEARCH_DEPARTMENT', 'Search department...'),
      },
      {
        fieldKey: 'employmentTypeId',
        label: t('K_FILTER_EMPLOYMENT_TYPE', 'Employment Type'),
        type: 'select',
        options: employmentTypeOptions,
        placeholder: t('K_PLACEHOLDER_SELECT_EMPLOYMENT_TYPE', 'Select employment type...'),
      },
      {
        fieldKey: 'contractType',
        label: t('K_FILTER_CONTRACT_TYPE', 'Contract Type'),
        type: 'text',
        placeholder: t('K_PLACEHOLDER_SEARCH_CONTRACT_TYPE', 'Search contract type...'),
      },
      {
        fieldKey: 'startDate',
        label: t('K_FILTER_START_DATE', 'Start Date'),
        type: 'date-range',
        fromLabel: t('K_LABEL_FROM', 'From'),
        toLabel: t('K_LABEL_TO', 'To'),
      },
      {
        fieldKey: 'legalEntityId',
        label: t('K_FILTER_LEGAL_ENTITY', 'Legal Entity'),
        type: 'select',
        options: legalEntityOptions,
        placeholder: t('K_PLACEHOLDER_SELECT_LEGAL_ENTITY', 'Select legal entity...'),
      },
    ],
    [t, countries, employmentTypeOptions, legalEntityOptions],
  );

  const {
    filters,
    hasFilters,
    activeFilterCount,
    isPanelOpen,
    togglePanel,
    closePanel,
    applyFilters,
    removeFilter,
    clearAllFilters,
    filterPayload,
  } = useTableFilters({
    routeTo: '/t/employees',
    filtersParam,
    fields: filterFields,
  });

  const columns = useMemo<DataTableColumn<EmployeeDto>[]>(
    () => [
      {
        id: 'name',
        header: t('K_COLUMN_NAME', 'Name'),
        sortable: true,
        sortKey: 'firstName',
        width: '220px',
        cell: (item) => {
          const displayName = item.preferredName || `${item.firstName} ${item.lastName}`;
          return (
            <span className={styles.nameCell} title={displayName}>
              <Avatar
                name={displayName}
                size="xs"
                color="blue"
                {...(item.profileImageUrl ? { src: item.profileImageUrl } : {})}
              />
              <span className={styles.cellPrimary}>{displayName}</span>
            </span>
          );
        },
      },
      {
        id: 'workEmail',
        header: t('K_COLUMN_EMAIL', 'Email'),
        cell: (item) => item.workEmail || '-',
      },
      {
        id: 'country',
        header: t('K_COLUMN_COUNTRY', 'Country'),
        cell: (item) => countryMap.get(item.countryId) ?? '-',
      },
      {
        id: 'jobTitle',
        header: t('K_COLUMN_JOB_TITLE', 'Job Title'),
        cell: (item) => item.jobTitle?.name ?? '-',
      },
      {
        id: 'department',
        header: t('K_COLUMN_DEPARTMENT', 'Department'),
        cell: (item) => item.department?.name ?? '-',
      },
      {
        id: 'employmentType',
        header: t('K_COLUMN_EMPLOYMENT_TYPE', 'Employment Type'),
        cell: (item) => employmentTypeMap.get(item.employmentTypeId) ?? item.employmentType ?? '-',
      },
      {
        id: 'contractType',
        header: t('K_COLUMN_CONTRACT_TYPE', 'Contract Type'),
        cell: (item) => item.contractType || '-',
      },
      {
        id: 'startDate',
        header: t('K_COLUMN_START_DATE', 'Start Date'),
        sortable: true,
        sortKey: 'startDate',
        cell: (item) => item.startDate
          ? new Date(item.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
          : '-',
      },
      {
        id: 'legalEntity',
        header: t('K_COLUMN_LEGAL_ENTITY', 'Legal Entity'),
        // Read embedded `legalEntity` from the BE list response (enriched via platform-service bulkResolve).
        // Side-loaded legalEntityMap is unreliable here — tenants don't hold ENTITIES_VIEW so getLegalEntities is disabled.
        cell: (item) => item.legalEntity?.name ?? '-',
      },
      {
        id: 'grossSalary',
        header: t('K_COLUMN_GROSS_SALARY', 'Gross Salary'),
        cell: (item) => item.grossSalary != null
          ? `${item.salaryCurrency} ${item.grossSalary.toLocaleString()}`
          : '-',
      },
      {
        id: 'salaryFrequency',
        header: t('K_COLUMN_FREQUENCY', 'Frequency'),
        cell: (item) => item.salaryFrequency || '-',
      },
      {
        id: 'status',
        header: t('K_COLUMN_STATUS', 'Status'),
        sortable: true,
        sortKey: 'status',
        cell: (item) => <StatusBadge status={item.status} />,
      },
      {
        id: 'created',
        header: t('K_COLUMN_CREATED', 'Created'),
        sortable: true,
        sortKey: 'createdOn',
        cell: (item) => (
          <span className={styles.cellSecondary}>
            {new Date(item.createdOn).toLocaleDateString()}
          </span>
        ),
      },
      {
        id: 'updated',
        header: t('K_COLUMN_UPDATED', 'Updated'),
        sortable: true,
        sortKey: 'updatedOn',
        cell: (item) => (
          <span className={styles.cellSecondary}>
            {new Date(item.updatedOn).toLocaleDateString()}
          </span>
        ),
      },
    ],
    [countryMap, employmentTypeMap, legalEntityMap, t],
  );

  const sortString = useMemo(() => buildSortString(sort, columns), [sort, columns]);

  const handleSortChange = useCallback(
    (newSort: SortState | undefined) => {
      void navigate({
        to: '/t/employees',
        search: (prev: TableSearchParams) => ({
          ...prev,
          page: 0,
          sortBy: newSort?.columnId,
          sortDir: newSort?.direction,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      void navigate({
        to: '/t/employees',
        search: (prev: TableSearchParams) => ({ ...prev, page: newPage }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageSizeChange = useCallback(
    (newSize: number) => {
      void navigate({
        to: '/t/employees',
        search: (prev: TableSearchParams) => ({ ...prev, size: newSize, page: 0 }),
        replace: true,
      });
    },
    [navigate],
  );

  const mergedPayload = useMemo(() => {
    const baseFilters = (filterPayload?.filters ?? {}) as Record<string, unknown>;
    if (activeTab === 'ALL') return filterPayload;
    return {
      ...filterPayload,
      filters: { ...baseFilters, status: activeTab },
    };
  }, [filterPayload, activeTab]);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['employees', { page, size, sort: sortString, filterPayload: mergedPayload }],
    queryFn: () => getEmployees({ page, size, sort: sortString }, mergedPayload),
  });

  const employees = data?.content ?? [];
  const pagination = useMemo(() => toPaginationState(data), [data]);
  const listForbidden = isForbiddenError(error);

  const getRowKey = useCallback((item: EmployeeDto) => item.id, []);

  const handleRowClick = useCallback(
    (item: EmployeeDto) => {
      void navigate({ to: '/t/employees/profile/$employeeId', params: { employeeId: item.id } });
    },
    [navigate],
  );

  const emptyState = useMemo(
    () =>
      listForbidden ? (
        <Forbidden variant="empty-state" hideAction />
      ) : (
        <div className={styles.emptyState}>
          <UsersEmptyIcon className={styles.emptyIcon} />
          <h2 className={styles.emptyTitle}>{t('K_EMPTY_NO_EMPLOYEES', 'No employees yet')}</h2>
          <p className={styles.emptyText}>
            {t('K_EMPTY_ADD_FIRST_EMPLOYEE', 'Get started by adding your first employee to the company.')}
          </p>
          {hasPermission('EMPLOYEES_CREATE') && (
            <Popover
              content={t('K_TOOLTIP_TENANT_NOT_ACTIVE', 'Client must be activated before adding employees')}
              trigger="hover"
              placement="bottom"
              disabled={isTenantActive}
            >
              <Button variant="filled" size="lg" leftIcon={<PlusIcon />} onClick={handleCreate} disabled={!isTenantActive}>
                {t('K_BUTTON_ADD_EMPLOYEE', 'Add Employee')}
              </Button>
            </Popover>
          )}
        </div>
      ),
    [listForbidden, handleCreate, t, isTenantActive, hasPermission],
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>{t('K_PAGE_TITLE_EMPLOYEES', 'Employees')}</h1>
          <p className={styles.subtitle}>{t('K_PAGE_SUBTITLE_EMPLOYEES', 'Manage your employees')}</p>
        </div>

        {hasPermission('EMPLOYEES_CREATE') && (
          <Popover
            content={t('K_TOOLTIP_TENANT_NOT_ACTIVE', 'Client must be activated before adding employees')}
            trigger="hover"
            placement="bottom"
            disabled={isTenantActive}
          >
            <Button variant="filled" size="lg" leftIcon={<PlusIcon />} onClick={handleCreate} disabled={!isTenantActive}>
              {t('K_BUTTON_ADD_EMPLOYEE', 'Add Employee')}
            </Button>
          </Popover>
        )}
      </div>

      <Tabs
        tabs={statusTabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        counts={tabCounts}
      />

      {hasFilters && (
        <AppliedFiltersBar
          fields={filterFields}
          values={filters}
          onRemove={removeFilter}
          onClearAll={clearAllFilters}
        />
      )}

      <DataTable
        columns={columns}
        data={employees}
        rowKey={getRowKey}
        onRowClick={handleRowClick}
        loading={isLoading}
        fetching={isFetching && !isLoading}
        loadingText={t('K_LOADING_EMPLOYEES', 'Loading employees...')}
        emptyState={emptyState}
        sort={sort}
        onSortChange={handleSortChange}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        showToolbar
        onFilterClick={togglePanel}
        activeFilterCount={activeFilterCount}
        aria-label="Employees list"
      />

      <FilterPanel
        isOpen={isPanelOpen}
        onClose={closePanel}
        fields={filterFields}
        values={filters}
        onApply={applyFilters}
        onReset={clearAllFilters}
        title={t('K_FILTER_TITLE', 'Filters')}
      />

      <CreateEmployeeModal isOpen={isModalOpen} onClose={handleModalClose} />
    </div>
  );
}
